/**
 * BundleBuilder-local RHF fork of CommonFields/FixedBoxesConfig.tsx (shared
 * with Placeholder, so not editable in place).
 */
import { useContext, useState } from "react";
import { FormLayout, Button, Card, Text, BlockStack, Icon, InlineStack, Tooltip } from "@shopify/polaris";
import { DeleteIcon, PlusIcon, PlusCircleIcon, InfoIcon } from "@shopify/polaris-icons";
import { FixedBoxConfig } from "../../Types";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import { getNumberFieldSuffix } from "../../../../utilities/constants";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FormTextField } from "../../../../components/FormFields";

const FixedBoxesConfig = () => {
    const { money_format } = useContext(AppConfigContext);
    const { control } = useFormContext();
    const { fields: boxes, replace } = useFieldArray({ control, name: "fixed_boxes" });
    const [activeBoxIndex, setActiveBoxIndex] = useState(0);

    const boxValues = boxes as unknown as FixedBoxConfig[];
    const activeBox = boxValues[activeBoxIndex];

    const handleAddBox = () => {
        let newItemCount = boxValues.length > 0 ? boxValues[boxValues.length - 1].itemsCount + 1 : 1;
        while (boxValues.some((box) => box.itemsCount === newItemCount)) {
            newItemCount++;
        }
        const newBox: FixedBoxConfig = {
            itemsCount: newItemCount,
            fixedPrice: boxValues.length > 0 ? boxValues[boxValues.length - 1].fixedPrice + 10 : 50,
            boxName: `Box of ${newItemCount}`,
        };
        replace([...boxValues, newBox]);
        setActiveBoxIndex(boxValues.length);
    };

    const handleRemoveBox = (index: number) => {
        if (boxValues.length <= 1) return;
        const newBoxes = boxValues.filter((_, i) => i !== index);
        replace(newBoxes);
        if (activeBoxIndex >= newBoxes.length) {
            setActiveBoxIndex(Math.max(0, newBoxes.length - 1));
        }
    };

    const handleUpdateBox = (index: number, key: keyof FixedBoxConfig, value: any) => {
        const newBoxes = boxValues.map((box, i) => (i === index ? { ...box, [key]: value } : box));
        replace(newBoxes);
    };

    return (
        <BlockStack gap="400">
            <BlockStack gap="200">
                <InlineStack gap="100" align="start">
                    <Text as="h3" variant="headingSm" fontWeight="bold">
                        2. Configure Your Boxes
                    </Text>
                    <Tooltip
                        content="Boxes are sorted by item quantity and unlock as customers add more products."
                        width="wide"
                    >
                        <Icon source={InfoIcon} tone="subdued" />
                    </Tooltip>
                </InlineStack>
                <Text as="p" variant="bodyMd">
                    Define the different box sizes and their fixed prices.
                </Text>
            </BlockStack>

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "24px",
                    overflowX: "auto",
                    borderBottom: "1px solid #E1E3E5",
                    width: 0,
                    minWidth: "100%",
                    flexWrap: "nowrap",
                }}
            >
                {boxValues.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setActiveBoxIndex(index)}
                        style={{
                            padding: "8px 16px",
                            backgroundColor: activeBoxIndex === index ? "#f4f6f8" : "transparent",
                            color: activeBoxIndex === index ? "#202223" : "#6D7175",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            fontWeight: activeBoxIndex === index ? "bold" : "normal",
                            fontSize: "14px",
                            border: "1px solid transparent",
                            borderBottom: activeBoxIndex === index ? "none" : "1px solid transparent",
                            borderRadius: "8px 8px 0 0",
                            flexShrink: 0,
                            ...(activeBoxIndex === index && {
                                border: "1px solid #E1E3E5",
                                borderBottom: "1px solid white",
                                marginBottom: "-1px",
                            }),
                        }}
                    >
                        Box {index + 1}
                    </button>
                ))}
                {boxValues.length < 10 && (
                    <button
                        onClick={handleAddBox}
                        style={{
                            padding: "8px 16px",
                            backgroundColor: "transparent",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            color: "#005BD3",
                            fontSize: "14px",
                            fontWeight: "500",
                            flexShrink: 0,
                        }}
                    >
                        <Icon source={PlusIcon} tone="interactive" />
                        Add Box
                    </button>
                )}
            </div>

            {activeBox && (
                <div style={{ backgroundColor: "#f4f6f8", borderRadius: "8px" }}>
                    <Card padding="400" background="bg-surface-secondary">
                        <BlockStack gap="400">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Text as="span" variant="bodyMd" fontWeight="bold">
                                    Configuring: Box {activeBoxIndex + 1}
                                </Text>
                                <Button
                                    icon={DeleteIcon}
                                    tone="critical"
                                    variant="tertiary"
                                    onClick={() => handleRemoveBox(activeBoxIndex)}
                                    disabled={boxValues.length <= 1}
                                >
                                    Delete
                                </Button>
                            </div>
                            <FormLayout>
                                <FormLayout.Group>
                                    <BlockStack gap="100">
                                        <FormTextField
                                            name={`fixed_boxes.${activeBoxIndex}.itemsCount`}
                                            label="Items in Box"
                                            type="number"
                                            value={activeBox.itemsCount.toString()}
                                            onChange={(val) =>
                                                handleUpdateBox(activeBoxIndex, "itemsCount", parseInt(val) || 0)
                                            }
                                            min={1}
                                            suffix="Items"
                                            error={
                                                activeBox.itemsCount <= 0
                                                    ? "Items count must be greater than zero"
                                                    : boxValues.some(
                                                            (box, i) =>
                                                                i !== activeBoxIndex &&
                                                                box.itemsCount === activeBox.itemsCount,
                                                        )
                                                      ? "Quantity already exists in another box"
                                                      : false
                                            }
                                        />
                                    </BlockStack>
                                    <FormTextField
                                        name={`fixed_boxes.${activeBoxIndex}.fixedPrice`}
                                        label="Fixed Price"
                                        type="number"
                                        value={activeBox.fixedPrice.toString()}
                                        onChange={(val) =>
                                            handleUpdateBox(activeBoxIndex, "fixedPrice", parseFloat(val) || 0)
                                        }
                                        suffix={getNumberFieldSuffix("fixed", money_format).suffix}
                                        min={0}
                                        error={
                                            activeBox.fixedPrice <= 0 ? "Fixed price must be greater than zero" : false
                                        }
                                    />
                                </FormLayout.Group>

                                <FormTextField
                                    name={`fixed_boxes.${activeBoxIndex}.boxName`}
                                    label="Box Name"
                                    type="text"
                                    value={activeBox.boxName || ""}
                                    onChange={(val) => handleUpdateBox(activeBoxIndex, "boxName", val)}
                                    error={!activeBox.boxName?.trim() ? "Box name cannot be empty" : false}
                                    maxLength={30}
                                    showCharacterCount
                                />
                            </FormLayout>
                        </BlockStack>
                    </Card>
                </div>
            )}

            {!activeBox && boxValues.length === 0 && (
                <div
                    style={{ textAlign: "center", padding: "40px", border: "1px dashed #E1E3E5", borderRadius: "8px" }}
                >
                    <BlockStack gap="200" align="center">
                        <Text as="p" tone="subdued">
                            No boxes configured yet.
                        </Text>
                        <Button onClick={handleAddBox} icon={PlusCircleIcon}>
                            Add your first box
                        </Button>
                    </BlockStack>
                </div>
            )}
            <BlockStack gap="200">
                <Text as="h3" variant="headingSm" fontWeight="bold">
                    3. Configure Your Messages
                </Text>
                <Card background="bg-surface-secondary">
                    <BlockStack gap="400">
                        <FormTextField
                            name="fixed_boxes_upsell_message"
                            label="Upsell Message"
                            placeholder="text"
                            maxLength={80}
                            showCharacterCount
                        />
                        <FormTextField
                            name="fixed_boxes_success_message"
                            label="Success Message"
                            placeholder="text"
                            maxLength={80}
                            showCharacterCount
                        />
                    </BlockStack>
                </Card>
            </BlockStack>
        </BlockStack>
    );
};

export default FixedBoxesConfig;
